import { useTranslation } from 'react-i18next';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Badge } from '@/components/atoms/badge';
import { Checkbox } from '@/components/atoms/checkbox';
import { Package } from 'lucide-react';
import { getImageUrl } from '@/lib/utils';
import type { VariationType, Product, CreateProductGroupItemDto } from '@/lib/types';

interface VariantConfigItemProps {
  product: Product;
  productId: string;
  currentProductId: string;
  config: CreateProductGroupItemDto;
  variationType: VariationType;
  index: number;
  onUpdateConfig: (productId: string, field: keyof CreateProductGroupItemDto, value: string | number | boolean) => void;
}

export function VariantConfigItem({
  product,
  productId,
  currentProductId,
  config,
  variationType,
  index,
  onUpdateConfig,
}: VariantConfigItemProps) {
  const { t } = useTranslation();
  const imageUrl = Array.isArray(product.images) ? getImageUrl(product.images[0]) : null;

  return (
    <div className="p-3 rounded-lg border space-y-3">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          {imageUrl ? (
            <img src={imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <Package className="size-5 m-auto text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{product.name}</p>
          {productId === currentProductId && (
            <Badge variant="secondary" className="text-xs">{t('associations.currentProduct')}</Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {(variationType === 'color' || variationType === 'size_color') && (
          <>
            <div className="space-y-1">
              <Label className="text-xs">{t('associations.colorName')}</Label>
              <Input
                value={config.colorName || ''}
                onChange={(e) => onUpdateConfig(productId, 'colorName', e.target.value)}
                placeholder={t('associations.colorNamePlaceholder')}
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t('associations.colorHex')}</Label>
              <div className="flex gap-1">
                <input
                  type="color"
                  value={config.colorHex || '#000000'}
                  onChange={(e) => onUpdateConfig(productId, 'colorHex', e.target.value)}
                  className="h-8 w-10 rounded border cursor-pointer"
                />
                <Input
                  value={config.colorHex || ''}
                  onChange={(e) => onUpdateConfig(productId, 'colorHex', e.target.value)}
                  placeholder="#000000"
                  className="h-8 text-sm flex-1"
                />
              </div>
            </div>
          </>
        )}

        {(variationType === 'size' || variationType === 'size_color') && (
          <div className="space-y-1">
            <Label className="text-xs">{t('associations.sizeName')}</Label>
            <Input
              value={config.sizeName || ''}
              onChange={(e) => onUpdateConfig(productId, 'sizeName', e.target.value)}
              placeholder="M, L, XL..."
              className="h-8 text-sm"
            />
          </div>
        )}

        {variationType === 'style' && (
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">{t('associations.styleName')}</Label>
            <Input
              value={config.styleName || ''}
              onChange={(e) => onUpdateConfig(productId, 'styleName', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}

        {variationType === 'pattern' && (
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">{t('associations.patternName')}</Label>
            <Input
              value={config.patternName || ''}
              onChange={(e) => onUpdateConfig(productId, 'patternName', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}

        {variationType === 'material' && (
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">{t('associations.materialName')}</Label>
            <Input
              value={config.materialName || ''}
              onChange={(e) => onUpdateConfig(productId, 'materialName', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}

        {variationType === 'flavor' && (
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">{t('associations.flavorName')}</Label>
            <Input
              value={config.flavorName || ''}
              onChange={(e) => onUpdateConfig(productId, 'flavorName', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}

        {variationType === 'quantity' && (
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">{t('associations.quantityLabel')}</Label>
            <Input
              value={config.quantityLabel || ''}
              onChange={(e) => onUpdateConfig(productId, 'quantityLabel', e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        )}

        <div className="space-y-1">
          <Label className="text-xs">{t('associations.displayOrder')}</Label>
          <Input
            type="number"
            value={config.displayOrder ?? index}
            onChange={(e) => onUpdateConfig(productId, 'displayOrder', parseInt(e.target.value))}
            min={0}
            className="h-8 text-sm"
          />
        </div>

        <div className="flex items-center gap-2 pt-5">
          <Checkbox
            id={`default-${productId}`}
            checked={config.isDefault || false}
            onCheckedChange={(checked) => onUpdateConfig(productId, 'isDefault', !!checked)}
          />
          <Label htmlFor={`default-${productId}`} className="text-xs">
            {t('associations.isDefault')}
          </Label>
        </div>
      </div>
    </div>
  );
}
